import React from "react";

import { Grid } from "@material-ui/core";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  CheckBoxBase,
  Input,
  QuickOptionsWithSelect,
  TextArea,
} from "components/Form/FormikControls";
import SlimButton, { SlimSecondaryButton } from "components/Button/SlimButton";
import { Row } from "components/Layout";

import {
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getMLAValidatedSectionsBySaleSubTypeId,
  getSaleSubTypeById,
  getSpeciesAttributes,
} from "selectors";
import {
  HasDeploymentMarks,
  OtherMarkingField,
  ProductSelectField,
  SaleLotLabelSelectorField,
} from "components/Form/Fields";
import { ForCattle, ForSheep } from "containers/ForSpecies";
import { DentitionField } from "components/Dentition/Index";
import { ForHooks, ForNotHooks } from "containers/ForSaleType";
import {
  useHasMLAOrDataPermission,
  useHasSaleyardOrDeploymentPermission,
  useToggle,
} from "hooks";
import MarkingField from "components/Form/Fields/MarkingField";
import { AnimalHealthField } from "components/Form/Fields/AnimalHealthField";
import { EIDExemptionReasonField } from "components/Form/Fields/EIDExemptionField";
import {
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { MobileTransactionalCreateSaleFormValues } from "..";
import { withNamespace } from "components/Form/FormikControls/lib";
import { FormCollapse } from "components/Form";
import { EstAvgWeightRangeField } from "components/WeightRange";
import { DressingRangeField } from "components/DressingRange";
import {
  FrameSectionSimple,
  PregStatusJoiningSection,
  SheepWeightSectionSimple,
} from "components/DetailedSaleLotModal/AdvancedDrafting/Sheep";
import { CattleWeightSectionSimple } from "components/DetailedSaleLotModal/AdvancedDrafting/Cattle";
import { SubtleBadge } from "components/Badge";
import { FaIcon } from "components/AgGridButtonIcon/agGridButtonIcon";
import { faTrash } from "@fortawesome/pro-solid-svg-icons";
import { defaultSkinLot } from "../types";

export const FormRow = styled(Row).attrs({
  fullWidth: true,
  flex: true,
  alignCenter: true,
  justifyBetween: true,
  marginVertical: 2,
})``;

interface WhatTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleNamespace: string;
  consignmentNamespace: string;
  saleLotNamespace: string;
  skinsLotNamespace: string;
}

export const WhatTab = ({
  deploymentId,
  speciesId,
  saleNamespace,
  consignmentNamespace,
  saleLotNamespace,
  skinsLotNamespace,
}: WhatTabProps) => {
  const { values, setFieldValue } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const skinsLotsHaveQuantity = values.skinLots.some(
    skinLot => !!skinLot.quantity,
  );

  const [showMoreSaleLotFields, toggleShowMoreSaleLotFields] = useToggle(false);
  const [includeSkins, toggleIncludeSkins] = useToggle(skinsLotsHaveQuantity);

  const handleToggleSkins = () => {
    if (includeSkins) {
      setFieldValue(skinsLotNamespace, [defaultSkinLot]);
      setFieldValue(
        withNamespace(consignmentNamespace, "billing_reference"),
        "",
      );
    }
    toggleIncludeSkins();
  };

  const {
    saleLot: {
      age_id: ageId,
      product_id: productId,
      sex_id: sexId,
      breed_id: breedId,
    },
    sale: { sale_type: saleType, sale_sub_type_id: saleSubTypeId },
  } = values;

  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );

  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );

  const { sexes } = useSelector(getSpeciesAttributes);

  // If saleyard permission we want to use that, otherwise fall back to deployment permission
  const hasWeightRangePerm = useHasSaleyardOrDeploymentPermission(
    DeploymentPermissions.featureWeightRange,
    SaleyardPermissions.featureWeightRange,
  );

  const dentitionOptions = useSelector(getDentitionOptionsByAgeId(ageId));

  // Conditionally render the est. carcass/live weight values and dressing % in the General section
  // otherwise these are rendered in advanced drafting

  const {
    mlaValidateWeightSection,
    mlaValidateJoiningSection,
    mlaValidateFrameSection,
    mlaValidateAge,
  } = useSelector(getMLAValidatedSectionsBySaleSubTypeId(saleSubTypeId));

  const saleSubType = useSelector(getSaleSubTypeById(saleSubTypeId)) || {};

  const areStudFeaturesEnabled = saleSubType.enableStudFeatures;
  const isDentitionEnabled = saleSubType.enableDentition;
  const isWeightRangeAndDressingEnabled =
    saleSubType.enableWeightRangeAndDressing;

  // User should have either MLA or DATA permission to see supplementary data fields
  const hasMLAOrDataPermission = useHasMLAOrDataPermission();

  // Conditionally render the *ranges* for weight and dressing in the General section
  const showWeightAndDressing =
    mlaValidateWeightSection &&
    isWeightRangeAndDressingEnabled &&
    hasWeightRangePerm;

  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);

  return (
    <>
      <Grid container item xs={12}>
        <FormRow>
          <Grid item xs={6}>
            <Input
              name={withNamespace(saleLotNamespace, "quantity")}
              label="Head"
              type="number"
              inputMode="numeric"
              required
            />
          </Grid>
          <ForSheep selectedSpeciesId={speciesId}>
            <ForHooks selectedSaleType={saleType}>
              <Grid item xs={5}>
                <CheckBoxBase
                  label="Include Skins"
                  onChange={handleToggleSkins}
                  value={includeSkins}
                />
              </Grid>
            </ForHooks>
          </ForSheep>
        </FormRow>

        <FormRow>
          <Grid item xs={12}>
            <ProductSelectField
              ns={saleLotNamespace}
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
              selectedSaleType={saleType}
            />
          </Grid>
        </FormRow>

        {/* Render age field for sheep sales, if required for MLA, if product selected, and */}
        {/* age was not determined by the selected product */}
        {mlaValidateAge && productId && !ageId && (
          <ForSheep selectedSpeciesId={speciesId}>
            <Grid item xs={12}>
              <QuickOptionsWithSelect
                ns={saleLotNamespace}
                name="age_id"
                label="Age"
                options={ageOptions}
                missingMLAFieldsWarningEnabled
              />
            </Grid>
          </ForSheep>
        )}

        <ForCattle selectedSpeciesId={speciesId}>
          <QuickOptionsWithSelect
            ns={saleLotNamespace}
            name="age_id"
            label="Age"
            options={ageOptions}
            missingMLAFieldsWarningEnabled
          />
        </ForCattle>

        {selectedSexHasProgeny && (
          <FormRow>
            <Grid item xs={12}>
              <Input
                type="number"
                label="Progeny Count"
                name={withNamespace(saleLotNamespace, "quantity_progeny")}
              />
            </Grid>
          </FormRow>
        )}

        {isDentitionEnabled && dentitionOptions.length > 0 && (
          <FormRow>
            <Grid item xs={12}>
              <DentitionField
                name={withNamespace(saleLotNamespace, "dentitionId")}
              />
            </Grid>
          </FormRow>
        )}

        <FormRow>
          <Grid item xs={12}>
            <QuickOptionsWithSelect
              ns={saleLotNamespace}
              name="breed_id"
              label="Breed"
              options={breedOptions}
              missingMLAFieldsWarningEnabled
            />
          </Grid>
        </FormRow>

        {includeSkins && (
          <>
            <FieldArray name={skinsLotNamespace}>
              {({ push, remove }) => (
                <>
                  {values.skinLots.map((_, index) => (
                    <FormRow key={index}>
                      <Grid item xs={4}>
                        <Input
                          name={`${skinsLotNamespace}[${index}].quantity`}
                          label={index === 0 ? "Skin Qty" : undefined}
                          type="number"
                          inputMode="numeric"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Input
                          name={`${skinsLotNamespace}[${index}].unitPrice`}
                          label={index === 0 ? "Skin Price" : undefined}
                          beforeSymbol="$"
                          type="number"
                          decimal
                        />
                      </Grid>
                      {/* offset the margin to take into account the height of the labels so it aligns nicely */}
                      <Grid
                        item
                        xs={2}
                        container
                        alignItems="flex-end"
                        style={index === 0 ? { marginBottom: "-18px" } : {}}
                      >
                        <SlimSecondaryButton
                          type="button"
                          fullWidth
                          disabled={index === 0 && values.skinLots.length === 1}
                          onClick={() => remove(index)}
                        >
                          <FaIcon icon={faTrash} />
                        </SlimSecondaryButton>
                      </Grid>
                    </FormRow>
                  ))}
                  <FormRow>
                    <Grid item xs={12}>
                      <Row justifyEnd>
                        <SlimButton
                          type="button"
                          onClick={() => push(defaultSkinLot)}
                        >
                          Add line
                        </SlimButton>
                      </Row>
                    </Grid>
                  </FormRow>
                </>
              )}
            </FieldArray>
            <FormRow>
              <Input
                label="External References/Kill Id"
                name={withNamespace(consignmentNamespace, "billing_reference")}
                maxLength={255}
                tooltip="This value will appear in the header of related Invoices/RCTI's and line items on the Invoice may be grouped by this value."
              />
            </FormRow>
          </>
        )}
      </Grid>

      <FormCollapse
        isOpen={showMoreSaleLotFields}
        onToggle={toggleShowMoreSaleLotFields}
        header={<SubtleBadge>More</SubtleBadge>}
      >
        <>
          {areStudFeaturesEnabled && (
            <>
              <FormRow>
                <Grid item xs={12}>
                  <Input
                    label="Tag #"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.tagNumber",
                    )}
                  />
                </Grid>
              </FormRow>

              <FormRow>
                <Grid item xs={12} data-tour="animalName">
                  <TextArea
                    label="Animal Name"
                    name={withNamespace(
                      saleLotNamespace,
                      "draftingAttributes.animalName",
                    )}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {showWeightAndDressing && (
            <>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <EstAvgWeightRangeField
                    ns={saleLotNamespace}
                    name="estimatedAverageWeightId"
                    saleRoundIdField={withNamespace(
                      saleNamespace,
                      "sale_round_id",
                    )}
                    disabled={false}
                    selectedSaleSubType={saleSubType}
                  />
                </Grid>
              </FormRow>
              <FormRow>
                <Grid item xs={6} md={12}>
                  <DressingRangeField
                    ns={saleLotNamespace}
                    name="dressingRangeId"
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </>
          )}
          {mlaValidateWeightSection && hasMLAOrDataPermission && (
            <>
              <ForSheep selectedSpeciesId={speciesId}>
                <FormRow>
                  <SheepWeightSectionSimple readOnly={false} />
                </FormRow>
              </ForSheep>
              <ForCattle selectedSpeciesId={speciesId}>
                <FormRow>
                  <CattleWeightSectionSimple readOnly={false} />
                </FormRow>
              </ForCattle>
            </>
          )}
          {mlaValidateJoiningSection && hasMLAOrDataPermission && (
            <FormRow>
              <PregStatusJoiningSection readOnly={false} />
            </FormRow>
          )}
          {mlaValidateFrameSection && <FrameSectionSimple readOnly={false} />}
          <ForNotHooks selectedSaleType={saleType}>
            <HasDeploymentMarks
              deploymentId={deploymentId}
              selectedSpeciesId={speciesId}
            >
              <FormRow>
                <Grid item xs={12}>
                  <MarkingField
                    deploymentId={deploymentId}
                    name={withNamespace(saleLotNamespace, "marks")}
                    selectedSpeciesId={speciesId}
                    disabled={false}
                  />
                </Grid>
              </FormRow>
            </HasDeploymentMarks>

            <FormRow>
              <Grid item xs={12}>
                <OtherMarkingField
                  deploymentId={deploymentId}
                  name={withNamespace(saleLotNamespace, "marks")}
                  disabled={false}
                  selectedSpeciesId={speciesId}
                />
              </Grid>
            </FormRow>
          </ForNotHooks>
          <FormRow>
            <Grid item xs={12}>
              <SaleLotLabelSelectorField
                label="Labels"
                name={withNamespace(saleLotNamespace, "labels")}
                deploymentId={deploymentId}
                selectedSpeciesId={speciesId}
              />
            </Grid>
          </FormRow>
          <FormRow>
            <Grid data-tour="category" item xs={12}>
              <AnimalHealthField
                ns={saleLotNamespace}
                disabled={false}
                isClearable
                selectedBreedId={breedId}
              />
            </Grid>
          </FormRow>
          <FormRow>
            <Grid item xs={12} data-tour="notes">
              <TextArea
                label="Sale Lot Notes"
                name={withNamespace(saleLotNamespace, "note")}
                disabled={false}
                rows={4}
              />
            </Grid>
          </FormRow>
          <ForNotHooks selectedSaleType={saleType}>
            <ForSheep selectedSpeciesId={speciesId}>
              <FormRow>
                <Grid data-tour="eid" item xs={12}>
                  <EIDExemptionReasonField
                    ns={saleLotNamespace}
                    isClearable
                    disabled={false}
                    selectedSpeciesId={speciesId}
                  />
                </Grid>
              </FormRow>
            </ForSheep>
          </ForNotHooks>
        </>
      </FormCollapse>
    </>
  );
};
